/**
 * AccountNotesSection — Customer Messaging Notes panel for AccountWorkspace.
 *
 * The SAME account-scoped store the GAS Notes tab uses (backend account_notes
 * via /api/v1/account-notes, logic in services/accountNotesCore.js). Access is
 * assignment-gated server-side: assigned AE/SC team, scope-all, or admin.
 * Trust boundary carve-out #6: user-typed messaging only; deletes are HARD.
 *
 * Composition (DEVELOPMENT_RULES rule 8): server state + mutations live in
 * useAccountNotes; NotesCapture / NotesList / ContextPacksList render it.
 * Vocabularies come from @techstack/core (contracts are law).
 *
 * Takes `account` (department acronym or id) — NOT the CRM account UUID, so
 * the panel works even before an accounts row exists for the department.
 */

import React from 'react';
import { StickyNote } from 'lucide-react';
import useAccountNotes from './useAccountNotes';
import NotesCapture from './NotesCapture';
import NotesList from './NotesList';
import ContextPacksList from './ContextPacksList';
import './AccountNotesSection.css';

/**
 * `compact` is the account-board contract: render the READING half only, and
 * only `limit` of it. On the tab this section is the whole notes workspace —
 * header, capture form, every note, context packs — which measured 13,118px
 * of content. Dropped into a widget cell that is a scrollbar around a page,
 * not a summary. The widget header already names the section and opens the
 * tab, so the header goes too.
 */
function AccountNotesSection({ account, compact = false, limit = 6, hideHeader = false }) {
  const api = useAccountNotes(account);
  const shown = compact ? { ...api, notes: api.notes.slice(0, limit) } : api;
  const hidden = compact ? Math.max(0, api.notes.length - limit) : 0;

  return (
    <div className={`aw-notes-section${compact ? ' aw-notes-compact' : ''}`}>
      {!compact && !hideHeader && (
        <div className="aw-notes-header">
          <StickyNote size={15} />
          <span className="aw-notes-title">Messaging Notes</span>
          <span className="aw-notes-count">{api.notes.length}</span>
        </div>
      )}
      {!compact && (
        <NotesCapture account={account} saveNote={api.saveNote} notice={api.notice} />
      )}
      <NotesList api={shown} compact={compact} />
      {/* Says what it is hiding rather than just ending — a truncated list with
          no marker reads as "that's all the notes there are". */}
      {hidden > 0 && (
        <p className="aw-notes-more">{hidden} more on the Notes tab</p>
      )}
      {!compact && <ContextPacksList api={api} />}
    </div>
  );
}

export default AccountNotesSection;
